import get from 'lodash/get'
import React from 'react'
import { connect } from 'react-redux'

import type { BusinessTypes } from '@disco/constants/business-type-v2'
import { LANGUAGE_MAP, LANGUAGE_MAP_OPTIONS } from '@disco/constants/languages'
import type { JobsType } from '@disco/constants/user-jobs'
import { JOBS_LABEL_MAP, JOBS_MAP } from '@disco/constants/user-jobs'
import {
  getLanguageOptions,
  LanguageMapKeys,
} from '@disco/i18n/language-options.js'
import type { WithTranslation } from '@disco/i18n/with-translation'
import { withTranslation } from '@disco/i18n/with-translation'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  deleteUser,
  updateUserSettings,
} from '@disco/state-management/actions/UserActions'
import {
  getBusinessTypeV2,
  getUserJob,
} from '@disco/state-management/selectors/user'

import type { DropdownStringOption } from 'types/ui'
import type { User } from 'types/user'

import RichTextEditor from 'components/common/rich-text-editor'
import Row from 'components/common/row'
import HelpTooltip from 'components/common/tooltip'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import ContactUsLink from 'components/ui/contact-us-link'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Label from 'components/ui/label'
import ModalDialog from 'components/ui/modal-dialog'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import TextField from 'components/ui/text-field'

function mapStateToProps(state) {
  return {
    user: state.user,
    businessType: getBusinessTypeV2(state),
    userJob: getUserJob(state),
  }
}

const mapDispatchToProps = {
  updateUserSettings,
  sendMixpanelEvent,
  deleteUser,
}
type Props = {
  user: User
  businessType: BusinessTypes
  userJob: JobsType
} & typeof mapDispatchToProps &
  WithTranslation

class AccountSettings extends React.Component<
  Props,
  {
    firstName: string
    lastName: string
    includeCustomMessage: boolean
    customMessage: string | null | undefined
    customMessageHtml: string | null | undefined
    showDeleteConfirmation: boolean
    userJob: JobsType
    language: LanguageMapKeys | undefined
  }
> {
  constructor(props: Props) {
    super(props)
    this.state = {
      firstName: props.user.first_name,
      lastName: props.user.last_name,
      customMessage: get(props.user, 'settings.custom_message', null),
      customMessageHtml: get(props.user, 'settings.custom_message_html', null),
      includeCustomMessage: get(
        props.user,
        'settings.include_custom_message',
        false,
      ),
      showDeleteConfirmation: false,
      userJob: props.userJob,
      language: props.user.language,
    }
  }

  componentDidMount() {
    this.props.sendMixpanelEvent('Open Profile & Account tab in settings')
  }

  _handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      firstName: event.target.value,
    })
  }
  _handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      lastName: event.target.value,
    })
  }
  _handleUserSettingsSubmit = () => {
    const {
      firstName,
      lastName,
      includeCustomMessage,
      customMessageHtml,
      customMessage,
      userJob,
      language,
    } = this.state
    const { settings } = this.props.user
    this.props.updateUserSettings(
      {
        first_name: firstName,
        last_name: lastName,
        settings: {
          include_custom_message: includeCustomMessage,
          custom_message: customMessage,
          custom_message_html: customMessageHtml,
        },
        business_user_settings: {
          job: userJob,
        },
        language,
      },
      true,
    )

    if (customMessage && customMessage !== settings.custom_message) {
      this.props.sendMixpanelEvent('Add or update contact details in settings')
    }
  }
  _customMessageIncludeChange = () => {
    this.setState({
      includeCustomMessage: !this.state.includeCustomMessage,
    })
  }
  _onCustomMessageChange = (
    customMessage: string | null | undefined,
    customMessageHtml: string | null | undefined,
  ) => {
    this.setState({
      customMessage,
      customMessageHtml,
    })
  }
  _handleUserJob = (option: DropdownStringOption) => {
    this.setState({
      userJob: (option?.value as JobsType) || null,
    })
  }
  _onProfileDeleteToggle = () =>
    this.setState({
      showDeleteConfirmation: !this.state.showDeleteConfirmation,
    })
  _handleLanguage = (option: { value: LanguageMapKeys }) => {
    this.setState({
      language: option.value,
    })
  }

  render() {
    const { t } = this.props
    const { email, business } = this.props.user
    const { firstName, lastName, includeCustomMessage, customMessage } =
      this.state
    const numberOfUsers = business ? business.stats.number_of_users : 0
    return (
      <>
        <DocumentHeadTags title="Settings | Profile & Account | DISCO" />
        <PageHeader
          title={`Profile & Account for ${this.props.user.first_name}`}
        />
        <PageContainer>
          <div className="Admin_Section">
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <TextField
                  label="First Name"
                  value={firstName}
                  onChange={this._handleFirstNameChange}
                />
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <TextField
                  label="Last Name"
                  value={lastName}
                  onChange={this._handleLastNameChange}
                />
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <HelpTooltip
                  noCssWrapper
                  base={
                    <TextField label="Email Address" value={email} readOnly />
                  }
                >
                  Need to change your email address?
                  <br />
                  <br />
                  <ContactUsLink>Contact us</ContactUsLink>
                </HelpTooltip>
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Label
                  caption="Contact details"
                  tooltipId="tooltip_settings_contact_details"
                />
                <RichTextEditor
                  className="CampaignDetails_Input"
                  // @ts-expect-error
                  initialContent={customMessage}
                  // @ts-expect-error
                  onChange={this._onCustomMessageChange}
                />
              </div>
            </Row>

            {this.props.businessType &&
              JOBS_MAP[this.props.businessType]?.length > 0 && (
                <Row columnsSize={[12]} marginBottom="small">
                  <div>
                    <Label
                      caption="What best describes your role?"
                      tooltipId="tooltip_what_best_describes_your_role"
                    />
                    <CustomDropdown
                      value={this.state.userJob}
                      options={JOBS_MAP[this.props.businessType]?.map(
                        value => ({
                          value,
                          label: JOBS_LABEL_MAP[value],
                        }),
                      )}
                      onChange={this._handleUserJob}
                    />
                  </div>
                </Row>
              )}

            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Label caption="Language" />
                <CustomDropdown
                  value={this.state.language}
                  options={getLanguageOptions(t)}
                  onChange={this._handleLanguage}
                />
              </div>
            </Row>

            <Row columnsSize={[12]}>
              <div>
                <Checkbox
                  label="Include contact details on my public DISCO pages"
                  checked={includeCustomMessage}
                  onChange={this._customMessageIncludeChange}
                />
              </div>
            </Row>
            <ButtonContainer>
              <Button
                appearance="primary"
                caption="Update Settings"
                progressCaption="Updating Settings"
                progressIndicator={this.props.user.updating}
                onClick={this._handleUserSettingsSubmit}
              />
              {business.private_metadata.self_serve_delete_profile && (
                <Button
                  appearance="danger"
                  caption="Delete my profile"
                  onClick={this._onProfileDeleteToggle}
                />
              )}
            </ButtonContainer>
          </div>
          {this.state.showDeleteConfirmation && (
            <ModalDialog
              title="Delete my profile"
              primaryAction={{
                caption: 'Confirm',
                onClick: this.props.deleteUser,
              }}
              status="danger"
              onCancel={this._onProfileDeleteToggle}
            >
              {numberOfUsers > 1
                ? 'Are you sure you want to permanently delete your profile?'
                : 'Are you sure you want to permanently delete your profile and your DISCO?'}
            </ModalDialog>
          )}
        </PageContainer>
      </>
    )
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation(['settings', 'common'])(AccountSettings)) as any
